import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "./api";
import useSocket from "../hooks/useSocket";
import TaxiIcon from "./TaxiIcon";

const timeOf = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

const OrderMessage = ({ message }) => {
  if (message.kind === "system") {
    return (
      <div className="py-0.5 text-center text-[1.1rem] text-secondary-text">
        {message.body}
      </div>
    );
  }
  if (message.kind === "action" || message.kind === "order_card") return null;

  return (
    <div
      className={`max-w-[88%] rounded-xl px-2.5 py-1.5 text-[1.3rem] leading-snug break-words ${
        message.mine ? "btn-accent ml-auto" : "glass"
      }`}
    >
      {!message.mine && message.senderName && (
        <div className="text-[1.05rem] text-secondary-text">{message.senderName}</div>
      )}
      {message.kind === "photo" ? (
        <img src={message.meta?.imageUrl} alt="Фото" className="max-h-[14rem] rounded" />
      ) : message.kind === "voice" ? (
        <audio controls src={message.meta?.voiceNoteUrl} />
      ) : (
        message.body
      )}
      <div className={`mt-0.5 text-right text-[1rem] ${message.mine ? "text-black/50" : "text-secondary-text"}`}>
        {timeOf(message.createdAt)}
      </div>
    </div>
  );
};

const OrderConversation = ({ orderId, canMarkRead = false, open = false, onToggle }) => {
  const { socket } = useSocket();
  const [available, setAvailable] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const history = await apiGet(`/api/orders/${orderId}/messages`);
      if (seq !== loadSeq.current) return;
      setMessages(history.data.messages ?? []);
      setAvailable(true);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      if (err.status === 403) {
        setAvailable(false);
        return;
      }
      setAvailable(true);
      setError(err.message);
    }
  }, [orderId]);

  useEffect(() => {
    load();
    const join = () => socket.emit("order:join", { orderId });
    const onMessage = (payload) => {
      if (String(payload?.orderId) !== String(orderId)) return;
      load();
    };
    join();
    socket.on("connect", join);
    socket.on("chat:message", onMessage);
    return () => {
      loadSeq.current += 1;
      socket.off("connect", join);
      socket.off("chat:message", onMessage);
      socket.emit("order:leave", { orderId });
    };
  }, [orderId, socket, load]);

  useEffect(() => {
    if (!open) return;
    const view = listRef.current;
    if (view) view.scrollTop = view.scrollHeight;
  }, [open, messages]);

  useEffect(() => {
    if (!open || !canMarkRead) return;
    apiPost(`/api/orders/${orderId}/read`, {}).catch(() => undefined);
  }, [open, canMarkRead, orderId, messages.length]);

  if (available !== true) return null;

  const last = messages[messages.length - 1];
  return (
    <div className="w-full">
      <button
        type="button"
        className="mt-1.5 flex w-full items-center gap-2 rounded-2xl border border-secondary-text/35 bg-secondary px-3 py-2 text-left"
        aria-expanded={open}
        onClick={onToggle}
      >
        <TaxiIcon name="message" size={17} />
        <span className="min-w-0 flex-1 truncate text-[1.3rem] font-semibold">
          Переписка по заказу #{String(orderId).slice(-6)}
        </span>
        <span className="shrink-0 text-[1.05rem] text-secondary-text">
          {messages.length || "нет"}
        </span>
        <TaxiIcon name={open ? "up" : "down"} size={15} />
      </button>

      {!open && last && (
        <div className="mt-0.5 px-2 truncate text-[1.15rem] text-secondary-text">
          {last.senderName ? `${last.senderName}: ` : ""}
          {last.body}
        </div>
      )}

      {open && (
        <div className="mt-1 overflow-hidden rounded-2xl border border-secondary-text/25 bg-[#101010]/80">
          <div ref={listRef} className="max-h-[26rem] space-y-1.5 overflow-y-auto p-2" aria-live="polite">
            {messages.length === 0 && (
              <div className="py-2 text-center text-[1.2rem] text-secondary-text">
                Сообщений пока нет
              </div>
            )}
            {messages.map((message) => (
              <OrderMessage key={message._id} message={message} />
            ))}
          </div>
          {error && <div role="alert" className="px-2 pb-1 text-[1.1rem] text-danger">{error}</div>}
          <div className="border-t border-secondary-text/20 px-2 py-1 text-center text-[1.05rem] text-secondary-text">
            Пишите в поле внизу — сообщение уйдёт участнику заказа
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderConversation;
